import { CATEGORY_LABELS, type CatalogSnapshot, type CategoryId, type DemoResponse, type HistogramBucket, type MetricsSnapshot, type ParticipantReceipt } from "./model";

const HISTOGRAM_RANGES: Array<Omit<HistogramBucket, "count">> = [
  { id: "0-249", label: "$0–249", minCents: 0, maxCents: 24_999 },
  { id: "250-499", label: "$250–499", minCents: 25_000, maxCents: 49_999 },
  { id: "500-749", label: "$500–749", minCents: 50_000, maxCents: 74_999 },
  { id: "750-999", label: "$750–999", minCents: 75_000, maxCents: 99_999 },
  { id: "1000-1499", label: "$1,000–1,499", minCents: 100_000, maxCents: 149_999 },
  { id: "1500-plus", label: "$1,500+", minCents: 150_000, maxCents: null },
];

export function formatMxn(cents: number, compact = false): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
    notation: compact ? "compact" : "standard",
  }).format(cents / 100);
}

export function createReceipt(
  responseId: string,
  selectedServiceIds: string[],
  catalog: CatalogSnapshot,
  updatedAt = Date.now(),
): ParticipantReceipt {
  const serviceMap = new Map(catalog.services.map((service) => [service.id, service]));
  const uniqueIds = [...new Set(selectedServiceIds)].filter((id) => serviceMap.has(id)).sort();
  const monthlyTotalCents = uniqueIds.reduce(
    (total, id) => total + (serviceMap.get(id)?.monthlyPriceCents ?? 0),
    0,
  );

  return {
    responseId,
    selectedServiceIds: uniqueIds,
    monthlyTotalCents,
    sixMonthTotalCents: monthlyTotalCents * 6,
    annualTotalCents: monthlyTotalCents * 12,
    updatedAt,
    catalogVerifiedAt: catalog.verifiedAt,
  };
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const midpoint = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[midpoint] ?? 0;
  return Math.round(((sorted[midpoint - 1] ?? 0) + (sorted[midpoint] ?? 0)) / 2);
}

export function calculateMetrics(
  sessionSlug: string,
  status: MetricsSnapshot["status"],
  responses: DemoResponse[],
  catalog: CatalogSnapshot,
  version = 1,
  lastSubmissionAt: number | null = null,
  connectedDashboards = 0,
): MetricsSnapshot {
  const services = new Map(catalog.services.map((service) => [service.id, service]));
  const serviceCounts = new Map<string, number>();
  const categoryCounts = new Map<CategoryId, number>();
  const categorySpend = new Map<CategoryId, number>();
  const totals: number[] = [];
  let totalServiceSelections = 0;

  for (const response of responses) {
    const selected = [...new Set(response.serviceIds)].filter((id) => services.has(id));
    const responseTotal = selected.reduce((total, id) => {
      const service = services.get(id);
      if (!service) return total;
      serviceCounts.set(id, (serviceCounts.get(id) ?? 0) + 1);
      categoryCounts.set(service.category, (categoryCounts.get(service.category) ?? 0) + 1);
      categorySpend.set(
        service.category,
        (categorySpend.get(service.category) ?? 0) + service.monthlyPriceCents,
      );
      return total + service.monthlyPriceCents;
    }, 0);
    totalServiceSelections += selected.length;
    totals.push(responseTotal);
  }

  const responseCount = responses.length;
  const totalMonthlyCents = totals.reduce((total, value) => total + value, 0);
  const histogram = HISTOGRAM_RANGES.map((range) => ({
    ...range,
    count: totals.filter(
      (total) => total >= range.minCents && (range.maxCents === null || total <= range.maxCents),
    ).length,
  }));

  return {
    type: "metrics",
    version,
    sessionSlug,
    status,
    responseCount,
    totalMonthlyCents,
    totalSixMonthsCents: totalMonthlyCents * 6,
    totalAnnualCents: totalMonthlyCents * 12,
    meanMonthlyCents: responseCount === 0 ? 0 : Math.round(totalMonthlyCents / responseCount),
    medianMonthlyCents: median(totals),
    averageServiceCount:
      responseCount === 0 ? 0 : Math.round((totalServiceSelections / responseCount) * 10) / 10,
    histogram,
    services: catalog.services.map((service) => {
      const count = serviceCounts.get(service.id) ?? 0;
      return {
        serviceId: service.id,
        name: service.shortName,
        category: service.category,
        count,
        adoptionRate: responseCount === 0 ? 0 : count / responseCount,
        monthlySpendCents: count * service.monthlyPriceCents,
      };
    }),
    categories: (Object.keys(CATEGORY_LABELS) as CategoryId[]).map((category) => ({
      category,
      count: categoryCounts.get(category) ?? 0,
      monthlySpendCents: categorySpend.get(category) ?? 0,
    })),
    lastSubmissionAt,
    connectedDashboards,
  };
}

export const DEMO_RESPONSES: DemoResponse[] = Array.from({ length: 34 }, (_, index) => {
  const patterns = [
    ["spotify", "netflix", "amazon-prime"],
    ["apple-music", "youtube-premium", "icloud"],
    ["spotify", "netflix", "disney-plus", "max"],
    ["spotify", "google-one", "notion"],
    ["netflix", "amazon-prime", "xbox-game-pass"],
    ["youtube-premium", "disney-plus", "playstation-plus"],
    ["spotify", "apple-music", "netflix", "google-one"],
    ["amazon-prime", "icloud"],
  ];
  return {
    participantToken: `demo-${index}`,
    serviceIds: patterns[index % patterns.length] ?? [],
  };
});

